import { api } from './client'

// Sales API client. Endpoints are expected to be provided by the Flask app
// under /api/sales. These functions are safe to call even if the backend
// hasn’t implemented them yet — callers should handle errors.

export const listSales = async ({ start, end, status, saleNo } = {}) => {
  const params = {}
  if (saleNo) params.sale_no = saleNo
  if (start) params.start = start
  if (end) params.end = end
  if (status) params.status = status
  const { data } = await api.get('/sales', { params })
  return data
}

export const getSaleDetail = async (saleId) => {
  const { data } = await api.get(`/sales/${saleId}`)
  return data
}

export const postSale = async (saleId) => {
  const { data } = await api.post(`/sales/${saleId}/post`)
  return data
}

export const listCustomerInvoices = async (params = {}) => {
  const { data } = await api.get('/sales/customer-invoices', { params })
  return Array.isArray(data) ? data : (data?.rows || [])
}

export const getCustomerInvoice = async (invoiceId) => {
  const { data } = await api.get(`/sales/customer-invoices/${invoiceId}`)
  return data
}

export const saveCustomerInvoice = async (payload) => {
  const invoiceId = payload?.invoice_id
  const { data } = invoiceId
    ? await api.put(`/sales/customer-invoices/${invoiceId}`, payload)
    : await api.post('/sales/customer-invoices', payload)
  return data
}

export const postCustomerInvoice = async (invoiceId) => {
  const { data } = await api.post(`/sales/customer-invoices/${invoiceId}/post`)
  return data
}

export const voidCustomerInvoice = async (invoiceId, payload = {}) => {
  const { data } = await api.post(`/sales/customer-invoices/${invoiceId}/void`, payload)
  return data
}

export const listOpenCustomerInvoices = async (params = {}) => {
  const { data } = await api.get('/sales/customer-invoices/open', { params })
  return Array.isArray(data) ? data : (data?.rows || [])
}

export const listCustomerQuotes = async (params = {}) => {
  const { data } = await api.get('/sales/customer-quotes', { params })
  return Array.isArray(data) ? data : (data?.rows || [])
}

export const getCustomerQuote = async (quoteId) => {
  const { data } = await api.get(`/sales/customer-quotes/${quoteId}`)
  return data
}

export const saveCustomerQuote = async (payload) => {
  const quoteId = payload?.quote_id
  const { data } = quoteId
    ? await api.put(`/sales/customer-quotes/${quoteId}`, payload)
    : await api.post('/sales/customer-quotes', payload)
  return data
}

export const sendCustomerQuote = async (quoteId) => {
  const { data } = await api.post(`/sales/customer-quotes/${quoteId}/send`)
  return data
}

export const voidCustomerQuote = async (quoteId, payload = {}) => {
  const { data } = await api.post(`/sales/customer-quotes/${quoteId}/void`, payload)
  return data
}

export const convertCustomerQuote = async (quoteId) => {
  const { data } = await api.post(`/sales/customer-quotes/${quoteId}/convert`)
  return data
}

export const listCustomerCreditMemos = async (params = {}) => {
  const { data } = await api.get('/sales/customer-credit-memos', { params })
  return Array.isArray(data) ? data : (data?.rows || [])
}

export const createCustomerCreditMemo = async (payload) => {
  const { data } = await api.post('/sales/customer-credit-memos', payload)
  return data
}

export const listCustomerPayments = async (params = {}) => {
  const { data } = await api.get('/sales/customer-payments', { params })
  return Array.isArray(data) ? data : (data?.rows || [])
}

export const saveCustomerPayment = async (payload) => {
  const { data } = await api.post('/sales/customer-payments', payload)
  return data
}

export const voidCustomerPayment = async (paymentId, payload = {}) => {
  const { data } = await api.post(`/sales/customer-payments/${paymentId}/void`, payload)
  return data
}

export const resolveProductByCode = async (code, locationId) => {
  const params = { code }
  if (locationId != null && String(locationId).trim() !== '') {
    params.location_id = Number(locationId)
  }
  const { data } = await api.get('/sales/product-by-code', { params })
  if (!data) return data
  const t = (data.discount_type || '').toLowerCase()
  const v = parseFloat(data.discount_value || 0)
  const up = parseFloat(data.unit_price || 0)
  let disc = 0
  if (t === 'percent') disc = Math.round(Math.max(0, Math.min(100, v)) * up) / 100
  if (t === 'amount') disc = Math.max(0, Math.min(v, up))
  return { ...data, discount: disc }
}

export const completeSale = async (payload) => {
  // payload: { payment_method, location_id, lines: [{product_id, sku, name, qty, price}], cash_received? }
  const { data } = await api.post('/sales/complete', payload)
  return data // { sale_id, subtotal, tax_amount, total, total_amount, je_id }
}

export const previewSale = async (payload) => {
  // payload: { location_id, lines: [{product_id, qty}] }
  const { data } = await api.post('/sales/preview', payload)
  return data // { location_id, products: [...], totals: { subtotal, tax_amount, total, ... } }
}

export const voidSale = async (saleId) => {
  const { data } = await api.post(`/sales/${saleId}/void`)
  return data
}

// Draft cleanup helpers
export const scanDrafts = async ({ start, end }) => {
  const { data } = await api.get('/sales/drafts/scan', { params: { start, end } })
  return data // { empty_count, refund_count, postable_count, refund_rows: [...] }
}

export const voidEmptyDrafts = async ({ start, end }) => {
  const { data } = await api.post('/sales/drafts/void-empty', { start, end })
  return data
}

export const postValidDrafts = async ({ start, end }) => {
  const { data } = await api.post('/sales/drafts/post-valid', { start, end })
  return data
}

// Refund workflow
export const refundStart = async (originalSaleId) => {
  const { data } = await api.post('/sales/refund/start', { sale_id: originalSaleId })
  return data // { refund_sale_id }
}

export const refundLines = async (refundSaleId) => {
  const { data } = await api.get(`/sales/refund/${refundSaleId}/lines`)
  return data // lines for refund draft
}

export const refundAddSku = async (refundSaleId, sku, qty, originalSaleId) => {
  const payload = { refund_sale_id: refundSaleId, sku, qty }
  if (originalSaleId) payload.original_sale_id = originalSaleId
  const { data } = await api.post('/sales/refund/add-sku', payload)
  return data
}

export const refundUpdateLine = async ({ refund_sale_id, line_id, quantity, unit_price }) => {
  const { data } = await api.patch('/sales/refund/line', { refund_sale_id, line_id, quantity, unit_price })
  return data
}

export const refundDeleteLine = async ({ refund_sale_id, line_id }) => {
  const { data } = await api.delete('/sales/refund/line', { data: { refund_sale_id, line_id } })
  return data
}

export const refundFinalize = async (refundSaleId, locationId) => {
  const { data } = await api.post('/sales/refund/finalize', { refund_sale_id: refundSaleId, location_id: locationId })
  return data // { je_id, refund_amount, total_cost }
}

export const cancelRefund = async (refundSaleId, deleteLines = true) => {
  const { data } = await api.post('/sales/refund/cancel', { refund_sale_id: refundSaleId, delete_lines: deleteLines })
  return data
}
